import React, { useState, useMemo } from 'react';
import { Input, Spin, Tag, Dropdown, Button, Modal } from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import {
  useCreateFlowLabel,
  useGetFlowLabels,
  useUpdateFlowLabel,
  useDeleteFlowLabel,
  useGetFlowList,
} from '@src/emp-3.0/api/queryHooks';

// Interface for the API response
interface IFlowLabel {
  id: number;
  label: string;
}

export const LabelMultiSelect = ({
  value,
  onChange,
}: {
  value?: number[];
  onChange?: (value: number[]) => void;
  mode?: string;
}) => {
  const { data: flowLabelsData = [], isLoading } = useGetFlowLabels();
  const { data: flowsData = [] } = useGetFlowList();
  const { mutateAsync: createLabel, isLoading: isCreatingLabel } = useCreateFlowLabel();
  const { mutateAsync: updateLabel, isLoading: isUpdatingLabel } = useUpdateFlowLabel();
  const { mutateAsync: deleteLabel, isLoading: isDeletingLabel } = useDeleteFlowLabel();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newLabelInput, setNewLabelInput] = useState('');
  const [editingLabelId, setEditingLabelId] = useState<number | null>(null);
  const [editLabelInput, setEditLabelInput] = useState('');

  const selectedValues = useMemo(() => value || [], [value]);

  // Helper function to find flows impacted by a label
  const getImpactedFlows = (labelId: number) => {
    return flowsData.filter((flow: any) => flow.labels && flow.labels.includes(labelId));
  };

  // Save edited label
  const handleSaveEditedLabel = async () => {
    if (editLabelInput.trim() && editingLabelId) {
      try {
        await updateLabel({ labelId: editingLabelId, labelName: editLabelInput.trim() });
        setEditingLabelId(null);
        setEditLabelInput('');
      } catch (error) {
        // Error is already handled by the hook's onError callback
        // Just prevent the promise from being uncaught
      }
    }
  };

  // Edit label handler - uses common action handler
  const handleEditLabel = (labelId: number, currentLabel: string) => {
    setEditingLabelId(labelId);
    setEditLabelInput(currentLabel);
  };

  // Delete label handler - performs the actual delete operation
  const handleDeleteLabel = async (labelId: number) => {
    try {
      await deleteLabel(labelId);

      // Remove from current selection if it was selected
      if (selectedValues.includes(labelId)) {
        const newValues = selectedValues.filter((id) => id !== labelId);
        onChange?.(newValues);
      }
    } catch (error) {
      // Error is already handled by the hook's onError callback
      // Just prevent the promise from being uncaught
    }
  };

  const handleLabelAction = (
    actionType: 'edit' | 'delete',
    labelId: number,
    currentLabel?: string,
  ) => {
    const impactedFlows = getImpactedFlows(labelId);
    if (impactedFlows.length > 0) {
      Modal.confirm({
        title: 'Changes to this label will impact other flows',
        icon: <ExclamationCircleOutlined style={{ fontSize: '16px', margin: '4px' }} />,
        content: (
          <div>
            <ul style={{ paddingLeft: '0', marginLeft: '0' }}>
              <li style={{ listStyle: 'none' }}>
                Impacted Flows: {impactedFlows.map((flow) => flow.name).join(', ')}
              </li>
            </ul>
          </div>
        ),
        async onOk() {
          if (actionType === 'edit') {
            handleEditLabel(labelId, currentLabel || '');
          } else if (actionType === 'delete') {
            await handleDeleteLabel(labelId);
          }
        },
        okText: 'Confirm',
        cancelText: 'Cancel',
        okType: 'danger',
      });
    } else {
      if (actionType === 'edit') {
        handleEditLabel(labelId, currentLabel || '');
      } else if (actionType === 'delete') {
        handleDeleteLabel(labelId);
      }
    }
  };

  const handleAddNewLabel = async () => {
    if (newLabelInput.trim()) {
      // Check if label already exists
      const existingLabel = flowLabelsData.find(
        (item: IFlowLabel) => item.label.toLowerCase() === newLabelInput.trim().toLowerCase(),
      );

      if (existingLabel) {
        // If label already exists, just add its ID to selection
        if (!selectedValues.includes(existingLabel.id)) {
          const newValues = [...selectedValues, existingLabel.id];
          onChange?.(newValues);
        }
        setNewLabelInput('');
        setIsAddingNew(false);
      } else {
        try {
          const labelName = newLabelInput.trim();
          // Create label in backend
          const response = await createLabel(labelName);
          setNewLabelInput('');
          setIsAddingNew(false);

          const newLabelId = response?.data?.id;
          if (newLabelId && !selectedValues.includes(newLabelId)) {
            const newValues = [...selectedValues, newLabelId];
            onChange?.(newValues);
          }
        } catch (error) {
          // Error is already handled by the hook's onError callback
          // Just prevent the promise from being uncaught
        }
      }
    }
  };

  if (isLoading) {
    return <Spin size="small" />;
  }

  return (
    <div
      style={{
        maxWidth: 'calc(100% - 44px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        rowGap: '16px',
      }}
    >
      {/* Predefined label options */}
      {flowLabelsData.map((item: IFlowLabel) => {
        const isEditing = editingLabelId === item.id;

        if (isEditing) {
          return isUpdatingLabel ? (
            <Spin size="small" />
          ) : (
            <Input
              key={item.id}
              size="small"
              value={editLabelInput}
              onChange={(e) => setEditLabelInput(e.target.value)}
              onPressEnter={() => handleSaveEditedLabel()}
              onBlur={() => handleSaveEditedLabel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setEditingLabelId(null);
                  setEditLabelInput('');
                }
              }}
              style={{ width: '120px' }}
              autoFocus
            />
          );
        }

        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleLabelAction('edit', item.id, item.label),
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            onClick: () => handleLabelAction('delete', item.id),
            danger: true,
          },
        ];

        return (
          <div
            key={item.id}
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={(e) => {
              const dropdown = e.currentTarget.querySelector('.kebab-menu') as HTMLElement;
              if (dropdown) dropdown.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const dropdown = e.currentTarget.querySelector('.kebab-menu') as HTMLElement;
              if (dropdown) dropdown.style.opacity = '0';
            }}
          >
            <Tag
              color={selectedValues.includes(item.id) ? 'green' : 'default'}
              onClick={() => {
                const isSelected = selectedValues.includes(item.id);
                if (isSelected) {
                  // Remove from selection
                  const newValues = selectedValues.filter((id) => id !== item.id);
                  onChange?.(newValues);
                } else {
                  // Add to selection
                  const newValues = [...selectedValues, item.id];
                  onChange?.(newValues);
                }
              }}
              style={{
                paddingRight: '24px', // Make space for the options button
                position: 'relative',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {selectedValues.includes(item.id) && (
                <CheckOutlined style={{ fontSize: 10, marginRight: 5 }} />
              )}
              {item.label}
            </Tag>
            <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                disabled={isUpdatingLabel || isDeletingLabel}
                className="kebab-menu"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '24px',
                  height: '24px',
                  padding: '0',
                  border: 'none',
                  background: 'transparent',
                  opacity: '0',
                  transition: 'opacity 0.2s ease-in-out',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        );
      })}

      {/* Add new label input or button */}
      {isCreatingLabel || isDeletingLabel ? (
        <Spin size="small" />
      ) : isAddingNew ? (
        <Input
          size="small"
          value={newLabelInput}
          onChange={(e) => setNewLabelInput(e.target.value)}
          onPressEnter={handleAddNewLabel}
          onBlur={handleAddNewLabel}
          placeholder="Enter label name"
          style={{ width: '120px' }}
          autoFocus
          disabled={isCreatingLabel}
        />
      ) : (
        <Tag
          onClick={() => setIsAddingNew(true)}
          style={{
            borderStyle: 'dashed',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          + New Label
        </Tag>
      )}
    </div>
  );
};
